import makeStyles from '@mui/styles/makeStyles';

//import levelUpBG from "../../assets/img/levelup-background-image.jpg";

export const useStyles = makeStyles(
  (theme) => ({
    page: {
      // Reserve space under the fixed top nav at every breakpoint so the
      // header stays sticky in PWA installs and small screens too.
      // Desktop nav is 64px; mobile nav stays 80 to leave room for the
      // taller mobile-only chrome (logo + avatar + larger tap targets).
      paddingTop: 64,
      // Mirror the header's height transition so page content slides up
      // smoothly as the agency row collapses on scroll.
      transition: "padding-top 220ms cubic-bezier(0.4, 0, 0.2, 1)",
      [theme.breakpoints.down('md')]: {
        paddingTop: 80,
      },
    },
    // Applied by AdminLayout when the user has >1 agency and we're on
    // mobile/tablet, where the AgencySelectionBox becomes a second row
    // INSIDE the fixed header card. The page must reserve the taller
    // header height so hero content (e.g. Dashboard hero) can still
    // bleed 16px up behind the header's rounded-bottom corners without
    // colliding with the top row.
    pageWithAgencySubheader: {
      [theme.breakpoints.down('md')]: {
        paddingTop: 136,
      },
    },
    // Applied whenever the mobile agency row is RENDERABLE (regardless
    // of its current expanded/collapsed state). Bakes in 56px of scroll
    // headroom — the exact delta the collapse animation removes from
    // the page — so the document never briefly becomes shorter than the
    // viewport while the row is animating shut. Without this, short
    // pages (content barely taller than the viewport) would oscillate:
    // collapse → page shrinks → scrollY snaps to 0 → useScrollTrigger
    // flips back to expanded → page grows → repeat (visible as a flash).
    pageWithMobileAgency: {
      [theme.breakpoints.down('md')]: {
        minHeight: "calc(100vh + 56px)",
      },
    },
    root: {
      [theme.breakpoints.down('md')]: {
        "& .MuiTabs-flexContainer": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "0px 32px",
        },
      },
    },
    header: {
      // The header is a vertical stack: `headerTopRow` is the always-
      // rendered top bar; on mobile/tablet a second `headerAgencyRow`
      // may be appended when the user belongs to >1 agency. Making the
      // header itself a flex column keeps the multi-row state on the
      // SAME white rounded card, so the hero's gradient bleeds through
      // the header's bottom-corner radius exactly like the single-row
      // case (see DashboardPage.styles `hero.marginTop: -16`).
      height: 64,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      // Match the page's padding-top transition so the rounded bottom
      // border + corners glide in lockstep with the agency row collapse.
      transition: "height 220ms cubic-bezier(0.4, 0, 0.2, 1)",
      // Sticky/fixed at every breakpoint — standard PWA top-nav pattern.
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      backgroundColor: "white",
      // Desktop: the bottom border is rendered via ::after so we can stop
      // it before the corner curve. The arc's top-right tangent sits at
      // column 76 (see AdminSidebar.styles.ts cornerCurve), so the border
      // starts at `left: 76` to butt seamlessly against the curve.
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: -1,
        left: 76,
        right: 0,
        height: 1,
        backgroundColor: "#DADFF6",
        pointerEvents: "none",
      },
      [theme.breakpoints.down('md')]: {
        // Mobile keeps the taller 80px bar to accommodate larger
        // tap targets and the mobile search trigger.
        height: 80,
        // Round only the bottom corners so the nav stays flush with the
        // screen edge at the top — a fully rounded bar feels detached on mobile.
        borderRadius: "0 0 16px 16px",
        // Mobile has no side rail, so the bottom border runs full-width.
        borderBottom: "1px solid #DADFF6",
        "&::after": {
          display: "none",
        },
      },
    },
    // When the AgencySelectionBox is rendered as a second header row on
    // mobile/tablet, grow the fixed header to 80 (top row) + 56 (agency
    // row) = 136 so the card contains both rows.
    headerWithAgency: {
      [theme.breakpoints.down('md')]: {
        height: 136,
      },
    },
    // Inner flex row that used to BE the header: holds the logo,
    // `topCenter` cluster, and the right-side avatar/search trigger.
    // Lives inside `.header` so desktop vs mobile heights are driven by
    // this row while the header itself can optionally grow taller for
    // the mobile agency row.
    headerTopRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      flex: "0 0 auto",
      height: 64,
      padding: "12px 24px",
      [theme.breakpoints.down('md')]: {
        height: 80,
        padding: 8,
      },
    },
    // Mobile/tablet only: sits below `headerTopRow` inside the same
    // fixed header card. Centers the AgencySelectionBox (400px-capped)
    // so tablet widths (600–899px) get a comfortable control instead of
    // the former 1/3-width grid cell.
    headerAgencyRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      flex: "0 0 auto",
      height: 56,
      padding: "0 16px 8px",
      // Clip the AgencySelectionBox while the row shrinks from 56→0
      // height so the field doesn't briefly bleed past the header's
      // rounded bottom corners during the transition.
      overflow: "hidden",
      // The collapse animation drives height + opacity + padding
      // simultaneously. Keep the durations aligned with the header /
      // page padding-top transitions so all three move as one unit.
      transition:
        "height 220ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease, padding 220ms cubic-bezier(0.4, 0, 0.2, 1)",
      [theme.breakpoints.down('sm')]: {
        padding: "0 8px 8px",
      },
    },
    // Applied to `headerAgencyRow` once the page has scrolled past the
    // threshold (see `useScrollTrigger` in AdminLayout). Drives the row
    // to zero height + opacity so it animates out of the header card
    // while preserving the rounded-bottom corner gradient bleed.
    headerAgencyRowCollapsed: {
      height: 0,
      opacity: 0,
      paddingTop: 0,
      paddingBottom: 0,
      pointerEvents: "none",
    },
    topLeft: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      fontSize: 20,
      fontWeight: 600,
      [theme.breakpoints.down('md')]: {
        gap: 8,
        fontSize: 16,
        marginLeft: 8,
      },
    },
    topCenter: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      // Desktop: 40px breathing room left of the agency selector, but only
      // 20px between the search field's trailing edge and the avatar so the
      // top-right cluster reads as a tighter group.
      margin: "0px 20px 0px 40px",
      [theme.breakpoints.down('md')]: {
        margin: 0,
      },
    },
    logo: {
      position: "relative",
      zIndex: 3,
      height: 30,
    },
    logoMark: {
      position: "relative",
      zIndex: 3,
      height: 32,
      width: 32,
      objectFit: "contain",
    },
    mobileSearchTrigger: {
      // Material guidelines: 48x48 minimum tap target.
      // IconButton size="large" provides 12px padding around a 24px icon = 48px.
      "& svg": {
        color: "#8D98C2",
      },
    },
    content: {
      position: "relative",
      // Desktop header is 64px (see `.header` above), so the content
      // area must reserve exactly that much vertical space below the
      // viewport top to fill the screen on short pages. The previous
      // `100vh - 80px` was inherited from the old 80px header height
      // and left a 16px gap below short pages on desktop.
      minHeight: "calc(100vh - 64px)",
      // Default desktop layout: leave 60px for the fixed side rail plus a
      // 25px gutter so page content doesn't butt up against the rail. We
      // intentionally do NOT set an explicit `width` here: block-level flow
      // naturally fills `body width - marginLeft`, which is the visible
      // viewport minus the rail. Using `width: 100vw - 88px` was the source
      // of a ~12px horizontal scrollbar, because `100vw` includes the
      // vertical scrollbar width while the parent's client width does not.
      marginLeft: 85,
      [theme.breakpoints.down('md')]: {
        // Mobile header is 80px (see `.header` mobile override). Bottom
        // nav is fixed and overlaid, so it does NOT need to be subtracted
        // here — the page's bottom padding already reserves room for it.
        minHeight: "calc(100vh - 80px)",
        marginLeft: 0,
        // Use `clip` (not `hidden`) so we still prevent horizontal overflow
        // without turning `.content` into a scrolling container — that
        // would trap `position: sticky` (used by the data grid column
        // headers) inside `.content` instead of the page viewport.
        overflowX: "clip",
        padding: "0 16px 80px 16px",
      },
    },
    // Per-page opt-out (see `Page.fullBleed` and `resolveActivePage`). Pages
    // that own their own edge-to-edge chrome set `fullBleed: true` in their
    // route config and the layout applies this class, which drops the 25px
    // gutter so content sits flush against the right edge of the side rail.
    contentFullBleed: {
      marginLeft: 60,
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
      },
    },
    agencyWrapper: {
      // Desktop-only wrapper that holds the AgencySelectionBox inline
      // with the search field / View site button. Mobile renders the
      // selector in its own header second row, so breakpoints below
      // `md` are intentionally unstyled here.
      display: "flex",
      alignItems: "center",
      // Cap at 400 — the same width as the sibling AdminSearchBox — so
      // the two controls read as equals and the selector never grows
      // wider than the search field (which was pushing the View site
      // button + search past the right edge of the header).
      maxWidth: 400,
      // Allow the wrapper (and the selector inside, which is now
      // `width: 100%`) to shrink inside `topCenter`'s space-between
      // layout at narrow desktop widths.
      flex: "1 1 auto",
      minWidth: 0,
      // Guarantee breathing room to the right cluster. `justify-content:
      // space-between` collapses to 0 gap once the flex children
      // overflow, which is what made the selector visually touch the
      // "View site" button at 900–1024px widths.
      marginRight: 24,
      [theme.breakpoints.down('lg')]: {
        // 900–1200px desktop: shrink the selector cap tighter so the
        // full-width search field (400) + View site button stay intact
        // on the right instead of being pushed past the header edge.
        maxWidth: 280,
      },
    },
    copyLinksWrapper: {
      [theme.breakpoints.down('md')]: {
        textAlign: "center",
        border: "1px solid #AFB6D4",
        borderRadius: "3px",
        height: 40,
        "& .MuiButton-label": {
          fontWeight: 300,
          textTransform: "capitalize",
        },
      },
    },
    linkText: {
      marginLeft: 12,
      fontWeight: "bold",
    },
  }),
  { classNamePrefix: "AdminLayout" },
);
